/**
 * RU — the default AND the fallback for every missing key (docs/06 §8).
 *
 * Merdan is the only user of this SPA. He is non-technical and Russian-speaking. This bundle
 * is the one that is actually read: it must be complete, precise, and free of developer
 * jargon. "Товар скрыт из каталога" — not "unpublished entity".
 *
 * Russian has three plural categories; every countable string defines one/few/many.
 */
export const ru = {
  common: {
    appName: 'MOST — админ-панель',
    loading: 'Загрузка…',
    save: 'Сохранить',
    cancel: 'Отмена',
    close: 'Закрыть',
    confirm: 'Подтвердить',
    edit: 'Изменить',
    view: 'Посмотреть',
    delete: 'Удалить',
    apply: 'Применить',
    reset: 'Сбросить',
    search: 'Поиск',
    filter: 'Фильтр',
    all: 'Все',
    none: '—',
    yes: 'Да',
    no: 'Нет',
    copy: 'Скопировать',
    copied: 'Скопировано',
    retry: 'Повторить',
    back: 'Назад',
    details: 'Подробнее',
    expand: 'Развернуть',
    collapse: 'Свернуть',
    page: 'Страница',
    of: 'из',
    prev: 'Назад',
    next: 'Вперёд',
    total: 'Всего',
    noRating: 'без рейтинга',
    desktopOnly: 'Админ-панель рассчитана на компьютер',
    desktopOnlyHint:
      'Экраны плотные, с таблицами и калькулятором наценки. Лучше открывать на компьютере — ширина от 1280 пикселей.',
    error: {
      title: 'Не удалось загрузить данные',
      description: 'Проверьте соединение и попробуйте ещё раз.',
      retry: 'Повторить запрос',
    },
    empty: {
      title: 'Пусто',
      nothingHere: 'Здесь пока ничего нет.',
    },
    liveOn: 'Обновления в реальном времени',
    liveOff: 'Нет связи с сервером — счётчики могут отставать',
    liveOffShort: 'Нет связи',
    liveConnecting: 'Подключение…',
    saving: 'Сохранение…',
    saved: 'Сохранено',
    saveFailed: 'Не удалось сохранить',
    unsavedChanges: 'Есть несохранённые изменения. Уйти со страницы?',
    // Units — must match the terminology table in docs/00.
    unit: {
      piece: 'штука',
      pack: 'пачка',
      box: 'упаковка',
      pallet: 'палета',
      truck: 'фура',
    },
    unitShort: {
      piece: 'шт',
      pack: 'пачка',
      box: 'упак.',
      pallet: 'палета',
      truck: 'фура',
    },
    perUnit: {
      piece: 'за штуку',
      pack: 'за пачку',
      box: 'за упаковку',
      pallet: 'за палету',
      truck: 'за фуру',
    },
  },

  nav: {
    dashboard: 'Дашборд',
    approvals: 'Одобрение',
    pricing: 'Наценка',
    orders: 'Заказы',
    inbox: 'Чат поддержки',
    users: 'Пользователи',
    categories: 'Категории',
    notifications: 'Уведомления',
    audit: 'Аудит',
    settings: 'Настройки',
    profile: 'Профиль',
    language: 'Язык',
    flavor: 'Цвет темы',
    theme: 'Светлая / тёмная тема',
    logout: 'Выйти',
    sectionQueues: 'Очереди',
    sectionManage: 'Управление',
    sectionSystem: 'Система',
  },

  palette: {
    open: 'Быстрый переход',
    placeholder: 'Куда перейти? Начните вводить…',
    empty: 'Ничего не найдено',
    sectionNav: 'Разделы',
    sectionActions: 'Действия',
    hintNavigate: 'перемещение',
    hintSelect: 'открыть',
    hintClose: 'закрыть',
    actions: {
      urgentPricing: 'Срочные товары — не видны покупателям',
      newOrders: 'Новые заказы',
      deadNotifications: 'Недоставленные уведомления',
      pendingApprovals: 'Заявки на одобрение',
    },
  },

  status: {
    user: {
      pending_email: 'Ждёт подтверждения email',
      pending_approval: 'Ждёт одобрения',
      active: 'Активен',
      blocked: 'Заблокирован',
    },
    order: {
      new: 'Новый',
      in_progress: 'В работе',
      completed: 'Завершён',
      cancelled: 'Отменён',
    },
    product: {
      draft: 'Черновик',
      active: 'Активен',
      archived: 'В архиве',
    },
    notification: {
      pending: 'В очереди',
      sent: 'Доставлено',
      failed: 'Ошибка',
      dead: 'Не доставлено',
    },
    channel: {
      telegram: 'Telegram',
      email: 'Email',
      inapp: 'В приложении',
      ws: 'WebSocket',
    },
    role: {
      buyer: 'Покупатель',
      producer: 'Производитель',
      admin: 'Администратор',
    },
    pricingFlag: {
      none: '—',
      base_price_changed: 'Изменена базовая цена',
      packaging_changed: 'Изменена упаковка',
    },
    market: {
      can_export: 'Может экспортировать',
      already_exports: 'Уже экспортирует',
    },
  },

  dashboard: {
    title: 'Дашборд',
    subtitle: 'Что требует внимания прямо сейчас',
    counters: {
      pendingApprovals: 'Производители на одобрении',
      unpricedProducts: 'Товары без наценки',
      urgentProducts: 'Срочно: скрыты из каталога',
      urgentHint: 'В очереди наценки и прямо сейчас невидимы покупателям',
      newOrders: 'Новые заказы',
      failedNotifications: 'Уведомления с ошибкой',
      deadNotifications: 'Уведомления не доставлены',
      deadHint:
        'Не доставленное уведомление админу означает, что заказ пришёл, а никто об этом не узнал.',
    },
    recentAudit: 'Последние события',
    recentAuditEmpty: 'Событий пока нет.',
    viewAllAudit: 'Весь журнал',
    allClear: 'Очереди пусты — ничего не ждёт вашего решения.',
    summary: {
      waiting_one: 'вопрос ждёт вашего решения',
      waiting_few: 'вопроса ждут вашего решения',
      waiting_many: 'вопросов ждут вашего решения',
      wrong_one: '{{count}} требует срочного вмешательства',
      wrong_few: '{{count}} требуют срочного вмешательства',
      wrong_many: '{{count}} требуют срочного вмешательства',
      live: 'Обновляется в реальном времени',
    },
    groups: {
      wrong: 'Что-то не так',
      waiting: 'Ожидает работы',
    },
  },

  approvals: {
    title: 'Одобрение производителей',
    subtitle: 'Анкеты со статусом «ждёт одобрения»',
    empty: 'Никто не ждёт одобрения.',
    columns: {
      brand: 'Бренд',
      email: 'Email',
      country: 'Страна',
      products: 'Товаров',
      submitted: 'Подана',
      status: 'Статус',
      actions: 'Действия',
    },
    resubmitted: 'Повторная подача',
    previousRejection: 'Причина прошлого отказа',
    groupBuyerVisible: 'Видно покупателям',
    groupMostOnly: 'Видно только MOST',
    fields: {
      brandName: 'Бренд',
      country: 'Страна',
      description: 'Описание',
      productionCapacity: 'Производственные мощности',
      rating: 'Рейтинг',
      legalName: 'Юридическое название',
      address: 'Адрес',
      contactPerson: 'Контактное лицо',
      phone: 'Телефон',
      email: 'Email',
      markets: 'Рынки экспорта',
      representativeContact: 'Контакт представителя',
      certificates: 'Сертификаты',
      noMarkets: 'Рынки не указаны',
      noCertificates: 'Сертификаты не загружены',
    },
    approve: 'Одобрить',
    reject: 'Отклонить',
    approveConfirmTitle: 'Одобрить производителя «{{brand}}»?',
    approveConfirmBody:
      'Аккаунт станет активным. Производитель сможет добавлять товары, и они попадут в вашу очередь наценки.',
    rejectConfirmTitle: 'Отклонить анкету «{{brand}}»?',
    rejectConfirmBody:
      'Производитель увидит причину дословно, исправит анкету и подаст её заново. Статус остаётся «ждёт одобрения» — отдельного статуса «отклонён» в системе нет.',
    rejectReasonLabel: 'Причина отказа',
    rejectReasonHint: 'Текст уйдёт производителю дословно. Напишите, что именно нужно исправить.',
    rejectReasonRequired: 'Укажите причину — она обязательна и уходит производителю.',
    approved: 'Производитель «{{brand}}» одобрен',
    rejected: 'Анкета «{{brand}}» отклонена, причина отправлена',
  },

  pricing: {
    title: 'Очередь наценки',
    subtitle: 'Ворота каталога: без продажной цены товар покупателям не виден',
    empty: 'Очередь пуста — все активные товары с наценкой.',
    emptyFiltered: 'В этой категории ничего нет.',
    filters: {
      all: 'Все',
      urgent: 'Срочно',
      flagged: 'Пересмотреть',
    },
    urgentBanner_one: '{{count}} товар не виден покупателям',
    urgentBanner_few: '{{count}} товара не видны покупателям',
    urgentBanner_many: '{{count}} товаров не видны покупателям',
    urgentBannerHint:
      'Без продажной цены товар не попадает в каталог. Установите цену, чтобы он снова продавался.',
    activatedOn: 'активирован {{date}}',
    whatChanged: 'Что изменилось',
    perPieceNote: '— цены за штуку',
    columns: {
      product: 'Товар',
      brand: 'Бренд',
      basePrice: 'Базовая цена (за шт.)',
      sellingPrice: 'Продажная цена (за шт.)',
      // The short forms, for the card list — it says "(за шт.)" once, in the header.
      base: 'Базовая',
      selling: 'Продажная',
      margin: 'Маржа',
      flag: 'Причина',
      activated: 'Активирован',
      actions: 'Действия',
    },
    urgentBadge: 'Срочно',
    urgentExplain: 'Товар скрыт из каталога до установки цены',
    repriceChip: 'Требует пересмотра',
    unpriced: 'Не оценён',
    oldBasePrice: 'Была базовая цена',
    newBasePrice: 'Стала базовая цена',
    marginAtCurrentPrice: 'Маржа при текущей продажной цене',
    marginNowNegative:
      'При новой базовой цене текущая продажная цена даёт отрицательную маржу. Пересмотрите цену.',
    setPrice: 'Установить цену',
    editPrice: 'Изменить цену',
    flaggedAt: 'Отмечен',

    calculator: {
      title: 'Калькулятор наценки',
      forProduct: '{{product}} · {{brand}}',
      basePriceSection: 'Базовая цена производителя',
      basePricePerPiece: 'За штуку (нормализовано)',
      basePriceContext: 'Для справки',
      enteredAs: 'Производитель ввёл: {{value}} {{unit}}',
      inputsSection: 'Наценка',
      markupPct: 'Наценка, %',
      sellingPrice: 'Продажная цена за штуку, $',
      syncHint: 'Поля связаны: измените любое — второе пересчитается.',
      previewSection: 'Что увидит покупатель',
      previewHint: 'Продажная цена на каждом уровне упаковки.',
      marginSection: 'Маржа',
      marginUsd: 'Маржа за штуку',
      marginPctLabel: 'Маржа, %',
      negativeMarginTitle: 'Отрицательная наценка',
      negativeMarginBody:
        'Продажная цена ниже базовой цены производителя — вы продаёте в убыток.',
      confirmNegative: 'Подтвердить отрицательную наценку',
      publish: 'Опубликовать',
      republish: 'Сохранить цену',
      withdraw: 'Снять с публикации',
      withdrawHint:
        'Очистит продажную цену. Товар мгновенно исчезнет из каталога покупателей и вернётся в эту очередь.',
      withdrawConfirmTitle: 'Снять «{{product}}» с публикации?',
      withdrawConfirmBody:
        'Продажная цена будет очищена. Товар сразу пропадёт из каталога и поиска покупателей и вернётся в очередь наценки как неоценённый. Уже поданные заказы не затрагиваются — в них цены зафиксированы.',
      priceRequired: 'Укажите продажную цену.',
      priceMustBePositive: 'Продажная цена должна быть больше нуля.',
      priceInvalid: 'Введите число.',
      confirmNegativeRequired:
        'Отметьте «подтвердить отрицательную наценку», чтобы сохранить цену ниже базовой.',
      publishConfirmTitle: 'Опубликовать «{{product}}» с отрицательной наценкой?',
      publishConfirmBody:
        'Продажная цена {{selling}} ниже базовой {{base}}. Маржа: {{margin}} ({{pct}}). Товар станет доступен покупателям немедленно.',
      published: 'Товар «{{product}}» опубликован',
      publishedHint: 'Он уже виден покупателям.',
      undo: 'Отменить',
      undone: 'Публикация отменена — товар снова скрыт',
      withdrawn: 'Товар «{{product}}» снят с публикации',
      storedNote:
        'Сохраняется только абсолютная цена за штуку. Процент нигде не хранится — иначе товар молча переоценивался бы при каждом изменении базовой цены.',
    },
  },

  orders: {
    title: 'Заказы',
    subtitle: 'Все заказы, с маржой',
    empty: 'Заказов пока нет.',
    columns: {
      number: 'Номер',
      date: 'Дата',
      buyer: 'Покупатель',
      status: 'Статус',
      items: 'Позиций',
      total: 'Сумма',
      margin: 'Маржа',
      truckFill: 'Загрузка фуры',
      actions: 'Действия',
    },
    filterStatus: 'Статус заказа',
    open: 'Открыть',
    changeStatus: 'Изменить статус',
    statusConfirmTitle: 'Перевести заказ {{number}} в «{{status}}»?',
    statusConfirmBody: 'Покупатель получит уведомление. Изменение попадёт в журнал аудита.',
    statusConfirmTerminal:
      'Это конечный статус — вернуть заказ в работу после него нельзя. Покупатель получит уведомление.',
    statusChanged: 'Заказ {{number}}: статус изменён',
    terminalHint: 'Заказ в конечном статусе — изменения недоступны.',

    /* Боковая панель заказа — быстрый просмотр состава, не замена полной страницы. */
    drawer: {
      title: 'Заказ {{number}}',
      buyer: 'Покупатель',
      items: 'Состав заказа',
      pieces: 'шт',
      weight: 'Вес',
      truckFill: 'Загрузка фуры',
      total: 'Сумма',
      margin: 'Маржа',
      openFull: 'Открыть полностью',
    },

    detail: {
      title: 'Заказ {{number}}',
      buyerSection: 'Покупатель',
      buyerHint: 'Полные контакты — с этим человеком нужно связаться.',
      company: 'Компания',
      contactPerson: 'Контактное лицо',
      phone: 'Телефон',
      email: 'Email',
      country: 'Страна',
      city: 'Город',
      rating: 'Рейтинг покупателя',
      ratingPrivate: 'Виден только вам — ни покупателю, ни производителям.',
      commentSection: 'Комментарий покупателя',
      commentHint: 'Только для вас. Производителям не пересылается.',
      noComment: 'Комментарий не оставлен.',
      compositionSection: 'Состав заказа',
      compositionHint: 'Сгруппировано по производителям. Каждый производитель видит только свою часть.',
      producerGroup: 'Производитель: {{brand}}',
      producerSubtotal: 'Итого по производителю',
      lineColumns: {
        product: 'Товар',
        qty: 'Количество',
        pieces: 'Штук',
        basePrice: 'Базовая (за шт.)',
        sellingPrice: 'Продажная (за шт.)',
        lineTotal: 'Сумма',
        lineMargin: 'Маржа',
        weight: 'Вес',
      },
      totalsSection: 'Итого',
      totalUsd: 'Сумма по продажным ценам',
      totalMargin: 'Маржа',
      totalWeight: 'Вес',
      truckFill: 'Загрузка фуры',
      truckFillOver: 'Заказ превышает одну фуру',
      notificationsSection: 'Доставка уведомлений',
      notificationsHint: 'Кому и по какому каналу ушёл этот заказ.',
      noNotifications: 'Уведомлений по этому заказу нет.',
    },
  },

  users: {
    title: 'Пользователи',
    subtitle: 'Покупатели и производители',
    empty: 'Пользователи не найдены.',
    searchPlaceholder: 'Email, компания, бренд…',
    filterRole: 'Роль',
    filterStatus: 'Статус',
    columns: {
      name: 'Название',
      email: 'Email',
      role: 'Роль',
      status: 'Статус',
      registered: 'Регистрация',
      lastLogin: 'Последний вход',
      actions: 'Действия',
    },
    neverLoggedIn: 'ни разу',
    detail: {
      title: 'Пользователь',
      profileSection: 'Профиль',
      telegramLinked: 'Telegram привязан',
      telegramNotLinked: 'Telegram не привязан',
      locale: 'Язык',
    },
    rating: {
      title: 'Рейтинг',
      producerPublic: 'Публичный рейтинг',
      producerPublicHint:
        'Виден покупателям — в карточках товаров и на странице производителя.',
      buyerPrivate: 'Приватная заметка',
      buyerPrivateHint:
        'Виден только вам. Никогда не показывается ни покупателю, ни производителям.',
      label: 'Оценка (1.0–5.0)',
      clear: 'Убрать рейтинг',
      invalid: 'Рейтинг — число от 1.0 до 5.0 с одним знаком после запятой.',
      saved: 'Рейтинг сохранён',
      cleared: 'Рейтинг убран',
    },
    block: 'Заблокировать',
    unblock: 'Разблокировать',
    blockConfirmTitle: 'Заблокировать «{{name}}»?',
    blockConfirmBody:
      'Все сессии пользователя будут немедленно прекращены — его выкинет из системы на всех устройствах. Действие обратимо.',
    unblockConfirmTitle: 'Разблокировать «{{name}}»?',
    unblockConfirmBody: 'Пользователь снова сможет войти в систему.',
    blocked: 'Пользователь заблокирован',
    unblocked: 'Пользователь разблокирован',
    verifyEmail: 'Отметить email подтверждённым',
    verifyEmailHint:
      'Крайняя мера, когда письмо не доходит. Действие записывается в журнал аудита.',
    verifyEmailConfirmTitle: 'Отметить email «{{email}}» подтверждённым?',
    verifyEmailConfirmBody:
      'Обычно пользователь подтверждает email сам, по ссылке из письма. Используйте это, только если письмо не доходит. Действие будет записано в журнал аудита.',
    verifyEmailDone: 'Email отмечен подтверждённым',

    create: {
      button: 'Создать пользователя',
      title: 'Создать пользователя вручную',
      hint: 'Запасной путь, когда производитель не может зарегистрироваться сам или письма не доходят. Аккаунт создаётся уже с подтверждённым email.',
      role: 'Роль',
      email: 'Email',
      locale: 'Язык интерфейса и уведомлений',
      companyName: 'Компания',
      contactPerson: 'Контактное лицо',
      phone: 'Телефон',
      country: 'Страна (код из 2 букв)',
      city: 'Город',
      brandName: 'Бренд',
      submit: 'Создать',
      created: 'Пользователь создан',
      passwordTitle: 'Временный пароль',
      passwordBody:
        'Пароль сгенерирован сервером и показывается ОДИН раз. Скопируйте его и передайте пользователю — например, в Telegram. Второй раз его получить нельзя.',
      passwordForceChange: 'При первом входе пользователь будет обязан сменить пароль.',
      passwordAck: 'Я скопировал пароль',
      passwordCopyFirst: 'Сначала скопируйте пароль — потом его будет не получить.',
      errors: {
        emailRequired: 'Укажите email.',
        emailInvalid: 'Некорректный email.',
        roleRequired: 'Выберите роль.',
        brandRequired: 'Для производителя нужен бренд.',
        companyRequired: 'Для покупателя нужна компания.',
        contactRequired: 'Для покупателя нужно контактное лицо.',
        phoneRequired: 'Для покупателя нужен телефон.',
        countryRequired: 'Укажите страну — код из 2 букв.',
        countryFormat: 'Код страны — 2 буквы, например RU или UZ.',
        cityRequired: 'Для покупателя нужен город.',
      },
    },
  },

  categories: {
    search: 'Поиск по названию или slug…',
    noResults: 'Ничего не найдено по запросу «{{query}}».',
    stats: {
      total: 'Категорий',
      active: 'Активных',
      products: 'Товаров в каталоге',
      subcategories: 'Подкатегорий',
      rootsHint_one: '{{count}} корневая',
      rootsHint_few: '{{count}} корневые',
      rootsHint_many: '{{count}} корневых',
      nestedHint_one: 'в {{count}} категории',
      nestedHint_few: 'в {{count}} категориях',
      nestedHint_many: 'в {{count}} категориях',
      activeHint: 'из {{total}} видны покупателям',
      productsHint_one: 'в {{count}} категории',
      productsHint_few: 'в {{count}} категориях',
      productsHint_many: 'в {{count}} категориях',
    },
    subcategories_one: '{{count}} подкатегория',
    subcategories_few: '{{count}} подкатегории',
    subcategories_many: '{{count}} подкатегорий',
    noSubcategories: 'Без подкатегорий',
    inactive: 'Отключена',
    expand: 'Показать подкатегории',
    collapse: 'Скрыть подкатегории',
    addSub: 'Добавить подкатегорию',
    title: 'Категории',
    subtitle: 'Названия на трёх языках, порядок, активность',
    empty: 'Категорий пока нет.',
    columns: {
      slug: 'Slug',
      nameRu: 'Название (RU)',
      nameEn: 'Название (EN)',
      nameTk: 'Название (TK)',
      sortOrder: 'Порядок',
      products: 'Товаров',
      active: 'Активна',
      actions: 'Действия',
    },
    draftNote:
      'Переводы EN и TK — черновые, до подтверждения владельцем. Их можно править здесь.',
    localeName: 'Язык названия',
    parentLabel: 'Родительская категория',
    parentNone: '— корневая категория —',
    parentHasChildrenHint:
      'У этой категории есть подкатегории, поэтому её нельзя сделать вложенной. Допускается один уровень вложенности.',
    add: 'Добавить категорию',
    edit: 'Изменить категорию',
    formTitleNew: 'Новая категория',
    formTitleEdit: 'Категория «{{name}}»',
    deactivateWarningTitle: 'Отключить категорию «{{name}}»?',
    deactivateWarningBody_one:
      '{{count}} товар исчезнет из каталога и поиска покупателей. В кабинете производителя и здесь он останется. Действие обратимо.',
    deactivateWarningBody_few:
      '{{count}} товара исчезнут из каталога и поиска покупателей. В кабинете производителя и здесь они останутся. Действие обратимо.',
    deactivateWarningBody_many:
      '{{count}} товаров исчезнут из каталога и поиска покупателей. В кабинете производителя и здесь они останутся. Действие обратимо.',
    deactivateWarningEmpty:
      'В этой категории нет товаров — отключение ни на что не повлияет. Действие обратимо.',
    deactivateWarningChildren:
      'Подкатегории тоже будут скрыты вместе со своими товарами.',
    noDelete: 'Категории не удаляются — только отключаются.',
    saved: 'Категория сохранена',
    saveFailed: 'Не удалось сохранить категорию',
    errors: {
      nameRuRequired: 'Название на русском обязательно.',
      nameEnRequired: 'Название на английском обязательно.',
      nameTkRequired: 'Название на туркменском обязательно.',
      sortOrderRequired: 'Укажите порядок сортировки.',
      sortOrderInteger: 'Порядок — целое число.',
    },
  },

  notifications: {
    title: 'Здоровье уведомлений',
    subtitle: 'Очередь отправки: что ушло, что упало, что не доставлено',
    empty: 'Уведомлений нет.',
    deadWarning_one:
      '{{count}} уведомление не доставлено. Проверьте — возможно, о заказе никто не узнал.',
    deadWarning_few:
      '{{count}} уведомления не доставлены. Проверьте — возможно, о заказах никто не узнал.',
    deadWarning_many:
      '{{count}} уведомлений не доставлено. Проверьте — возможно, о заказах никто не узнал.',
    columns: {
      created: 'Создано',
      recipient: 'Получатель',
      type: 'Тип',
      channel: 'Канал',
      status: 'Статус',
      attempts: 'Попыток',
      nextRetry: 'Следующая попытка',
      lastError: 'Последняя ошибка',
      sentAt: 'Отправлено',
      actions: 'Действия',
    },
    forAdmin: 'Вам',
    showDead: 'Показать мёртвые',
    refreshing: 'Обновление…',
    emptyFiltered: 'По этому фильтру уведомлений нет.',
    clearFilters: 'Сбросить фильтры',
    stats: {
      pending: 'В очереди',
      pendingHint: 'Ожидают доставки',
      failed: 'Сбой',
      failedHint: 'Ещё повторяются автоматически',
      dead: 'Мёртвые',
      deadHint: 'Попытки исчерпаны — нужен ручной повтор',
    },
    // Human-readable labels for the backend's notification-type registry
    // (backend/internal/notifications/domain.go). Raw code stays in the tooltip.
    types: {
      order_created_admin: 'Новый заказ — уведомление вам',
      order_created_producer: 'Новый заказ — уведомление производителю',
      order_created_buyer: 'Новый заказ — подтверждение покупателю',
      order_status_changed: 'Статус заказа изменён',
      producer_approved: 'Производитель одобрен',
      producer_rejected: 'Производитель отклонён',
      product_priced: 'Товар опубликован — цена установлена',
      email_verification: 'Подтверждение email',
      password_reset: 'Сброс пароля',
      product_unpublished_margin_guard: 'Товар снят с продажи — защита маржи',
      chat_message_admin: 'Сообщение в чате поддержки',
    },
    filterStatus: 'Статус',
    filterChannel: 'Канал',
    filterType: 'Тип',
    filterTypePlaceholder: 'например order_created',
    retry: 'Повторить',
    retryHint: 'Вернёт уведомление в очередь: статус «в очереди», счётчик попыток обнулится.',
    retryConfirmTitle: 'Повторить отправку?',
    retryConfirmBody:
      'Уведомление вернётся в очередь со статусом «в очереди», счётчик попыток обнулится. Действие записывается в журнал аудита.',
    retried: 'Уведомление возвращено в очередь',
    inspect: 'Посмотреть payload',
    payloadTitle: 'Payload уведомления',
    payloadHint:
      'Это ровно то, что получил адресат: payload отфильтрован по аудитории в момент записи, а не при чтении.',
    noError: 'Ошибок не было.',
  },

  audit: {
    title: 'Журнал аудита',
    subtitle: 'История действий. Только чтение — записи не редактируются и не удаляются.',
    empty: 'Записей нет.',
    columns: {
      when: 'Когда',
      actor: 'Кто',
      action: 'Действие',
      entity: 'Объект',
      entityId: 'ID объекта',
    },
    system: 'Система',
    filterActor: 'ID актора',
    filterAction: 'Действие',
    filterActionPlaceholder: 'например product.price_set',
    filterEntity: 'Объект',
    filterEntityPlaceholder: 'например product',
    filterFrom: 'С даты',
    filterTo: 'По дату',
    meta: 'Детали',
    noMeta: 'Деталей нет.',
    // Human-readable labels for the backend's audit registry
    // (backend/internal/admin/audit.go). The dotted verb stays in a tooltip; an unknown value
    // falls back to its raw code. Looked up with keySeparator disabled, so the dots are literal.
    actions: {
      'producer.approved': 'Производитель одобрен',
      'producer.rejected': 'Производитель отклонён',
      'producer.rated': 'Оценка производителя',
      'buyer.rated': 'Оценка покупателя',
      'user.created': 'Пользователь создан',
      'user.blocked': 'Пользователь заблокирован',
      'user.unblocked': 'Пользователь разблокирован',
      'user.email_verified_manually': 'Email подтверждён вручную',
      'product.price_set': 'Цена товара установлена',
      'category.created': 'Категория создана',
      'category.updated': 'Категория изменена',
      'order.status_changed': 'Статус заказа изменён',
      'notification.retried': 'Уведомление отправлено повторно',
      'role.created': 'Роль создана',
      'role.updated': 'Роль изменена',
      'role.deleted': 'Роль удалена',
      'role.assigned': 'Роль назначена',
    },
    entities: {
      producer: 'Производитель',
      product: 'Товар',
      user: 'Пользователь',
      order: 'Заказ',
      buyer: 'Покупатель',
      category: 'Категория',
      role: 'Роль',
      notification: 'Уведомление',
    },
  },

  settings: {
    title: 'Настройки',
    subtitle: 'Мониторинг, роли и права, журнал аудита.',
    tabs: {
      monitoring: 'Мониторинг',
      roles: 'Роли и права',
      audit: 'Журнал аудита',
    },
    categories: {
      approvals: 'Одобрения',
      pricing: 'Ценообразование',
      orders: 'Заказы',
      users: 'Пользователи',
      catalog: 'Каталог',
      system: 'Система',
    },
    monitoring: {
      title: 'Мониторинг',
      subtitle: 'Состояние системы. Только чтение — обновляется автоматически.',
      infra: 'Инфраструктура',
      infraHint: 'Связь с хранилищами и живой канал.',
      database: 'База данных',
      redis: 'Redis',
      liveChannel: 'Живой канал',
      outbox: 'Доставка уведомлений',
      outboxHint: 'Воркер очереди. Растущее «в очереди» означает, что доставка отстаёт.',
      pending: 'В очереди',
      pendingHint: 'Ожидают доставки',
      failed: 'Сбой',
      dead: 'Мёртвые',
      deadHint: 'Отказано — нужен ручной повтор',
      queues: 'Рабочие очереди',
      pendingApprovals: 'Ждут одобрения',
      unpriced: 'Без цены',
      urgent: 'Срочные (без цены и активны)',
      newOrders: 'Новые заказы',
      constants: 'Константы фуры',
      truckPallets: 'Палет на фуру',
      truckMaxKg: 'Макс. кг на фуру',
    },
    roles: {
      title: 'Роли и права',
      subtitle:
        'Роли объединяют права администратора. Они не влияют на анонимность покупателей и производителей.',
      create: 'Новая роль',
      createTitle: 'Новая роль',
      editTitle: 'Изменить роль',
      viewTitle: '{{name}} (встроенная)',
      dialogHint: 'Роль даёт набор прав администратора. Покупателей и производителей это не касается.',
      empty: 'Ролей пока нет.',
      name: 'Название',
      description: 'Описание',
      capabilities: 'Права',
      builtin: 'Встроенная',
      permCount: '{{count}} прав',
      permCount_one: '{{count}} право',
      permCount_few: '{{count}} права',
      memberCount: '{{count}} участников',
      memberCount_one: '{{count}} участник',
      memberCount_few: '{{count}} участника',
      created: 'Роль создана.',
      saved: 'Роль сохранена.',
      deleted: 'Роль удалена.',
      deleteTitle: 'Удалить роль «{{name}}»?',
      deleteHint:
        'Администраторы с этой ролью получат полный доступ. Ни один аккаунт не будет удалён.',
      errors: {
        nameRequired: 'Укажите название.',
        nameTaken: 'Роль с таким названием уже существует.',
      },
    },
  },

  profile: {
    title: 'Профиль',
    subtitle: 'Ваш аккаунт: язык, пароль и Telegram',
    account: {
      title: 'Аккаунт',
    },
    language: {
      title: 'Язык',
      hint: 'Язык интерфейса и уведомлений — на нём приходят сообщения в Telegram и письма.',
      saved: 'Язык сохранён',
    },
    password: {
      title: 'Смена пароля',
      hint: 'После смены пароля все остальные сессии будут завершены. Эта останется.',
      current: 'Текущий пароль',
      new: 'Новый пароль',
      newHint: 'Минимум 10 символов.',
      confirm: 'Новый пароль ещё раз',
      submit: 'Сменить пароль',
      changed: 'Пароль изменён. Остальные сессии завершены.',
      // Ключи required / too_short / incorrect совпадают с кодами ошибок сервера.
      errors: {
        required: 'Заполните поле.',
        too_short: 'Пароль — минимум 10 символов.',
        mismatch: 'Пароли не совпадают.',
        incorrect: 'Текущий пароль неверен.',
      },
    },
    telegram: {
      title: 'Telegram',
      hint: 'Основной канал уведомлений о заказах и очередях — надёжнее email в регионе.',
      linked: 'Telegram привязан',
      linkedAt: 'привязан {{date}}',
      notLinked: 'Telegram не привязан — уведомления приходят только на email и в приложение.',
      connect: 'Привязать Telegram',
      connectAgain: 'Открыть ссылку ещё раз',
      awaiting: 'Ждём привязку — подтвердите в Telegram и вернитесь на эту вкладку.',
      unlink: 'Отвязать',
      unlinked: 'Telegram отвязан. Уведомления будут приходить на email и в приложение.',
      linkError: 'Не удалось создать ссылку. Попробуйте ещё раз.',
    },
  },

  errors: {
    forbidden: 'Доступ запрещён',
    forbiddenBody: 'Этот раздел доступен только администратору.',
    goToOwnApp: 'Перейти в свой раздел',
    accountUnavailable: 'Аккаунт недоступен',
    accountUnavailableBody: 'Свяжитесь с MOST.',
    mustChangePassword: 'Требуется смена пароля',
    mustChangePasswordBody: 'Войдите со своим временным паролем и смените его в своём разделе.',
    notFound: 'Страница не найдена',
    notFoundBody: 'Такой страницы нет.',
    goHome: 'На дашборд',
    conflict: 'Состояние изменилось',
    conflictBody: 'Данные успели измениться. Обновите страницу и попробуйте ещё раз.',
    generic: 'Что-то пошло не так',
    genericBody: 'Повторите попытку. Если ошибка повторяется — сообщите разработчику.',
  },
  inbox: {
    title: 'Чат поддержки',
    emptyList: 'Пока никто не писал',
    noThread: 'Выберите диалог',
    emptyThread: 'Сообщений пока нет',
    placeholder: 'Ответить…',
    send: 'Отправить',
    attach: 'Прикрепить файл',
    record: 'Записать голосовое сообщение',
    stopRecording: 'Остановить запись',
    loadOlder: 'Показать более ранние',
    today: 'Сегодня',
    yesterday: 'Вчера',
    scrollDown: 'К последним сообщениям',
    sendError: 'Не удалось отправить. Попробуйте ещё раз.',
    callAudio: 'Аудиозвонок',
    callVideo: 'Видеозвонок',
    role: { buyer: 'Покупатель', producer: 'Производитель' },
    kind: { text: 'Сообщение', image: 'Изображение', video: 'Видео', audio: 'Аудио', file: 'Файл', call: 'Звонок' },
    call: {
      outgoing: 'Звоним…',
      incoming: 'Входящий звонок',
      connecting: 'Соединение…',
      active: 'Идёт разговор',
      endedStatus: 'Звонок завершён',
      accept: 'Ответить',
      decline: 'Отклонить',
      hangUp: 'Завершить',
      completed: 'Звонок',
      missed: 'Пропущенный звонок',
      declined: 'Звонок отклонён',
      mediaDenied: 'Нет доступа к микрофону или камере',
      connectionLost: 'Связь потеряна',
      failed: 'Не удалось начать звонок',
    },
  },
} as const;

export type AdminResources = typeof ru;
